'use client';

import React, { useEffect, useRef } from 'react';

export default function AboutSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal-up').forEach((el, i) => {
              setTimeout(() => el.classList.add('in-view'), i * 120);
            });
          }
        });
      },
      { threshold: 0.15 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section id="about" ref={sectionRef} className="bg-ink py-24 px-6 md:px-10 lg:px-14">
      <div className="max-w-5xl mx-auto">
        {/* Label */}
        <div className="reveal-up mb-12">
          <span className="section-label text-coral">About</span>
        </div>

        {/* Asymmetric layout: big text left, details right */}
        <div className="flex flex-col lg:flex-row gap-16 lg:gap-24 items-start">
          {/* Left — big statement */}
          <div className="flex-1 reveal-up">
            <h2 className="font-mono font-bold text-concrete leading-[0.92] tracking-tight"
              style={{ fontSize: 'clamp(2.2rem, 5.5vw, 4.5rem)' }}>
              A NEW EYE<br />
              ON THE<br />
              <span className="text-coral">WORLD.</span>
            </h2>
          </div>

          {/* Right — personal story */}
          <div className="flex-1 flex flex-col gap-8">
            <p className="reveal-up font-sans text-base md:text-lg text-concrete/65 leading-relaxed font-medium">
              I&apos;m Seferis Toni — 16 years old, based in Galați, Romania. Photography found me before I found it. I picked up a camera and suddenly the world looked different: every shadow had weight, every glance told a story.
            </p>
            <p className="reveal-up delay-1 font-sans text-base md:text-lg text-concrete/65 leading-relaxed font-medium">
              I&apos;m new to the industry, but I bring something that can&apos;t be taught — a genuine hunger to capture what&apos;s real. No over-editing, no manufactured moments. Just honest frames that make you feel something.
            </p>

            {/* Divider line */}
            <div className="reveal-up delay-2 w-12 h-[2px] bg-coral" />

            {/* Signature detail */}
            <div className="reveal-up delay-3 flex flex-col gap-1">
              <span className="font-mono text-[0.65rem] font-bold tracking-[0.35em] uppercase text-concrete/30">
                Photography name
              </span>
              <span className="font-mono text-[1.1rem] font-bold tracking-[0.1em] text-concrete">
                Hvz<span className="text-coral">.photos</span>
              </span>
            </div>
          </div>
        </div>

        {/* Bottom bento — shoot types */}
        <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-px bg-concrete/10 border border-concrete/10">
          {[
            { type: 'Portrait', desc: 'Raw, unposed moments' },
            { type: 'Street', desc: 'Life in motion' },
            { type: 'Event', desc: 'Stories as they happen' },
            { type: 'Creative', desc: 'Concepts & emotion' },
          ]?.map((item) => (
            <div key={item?.type} className="reveal-up bg-ink p-6 flex flex-col gap-2 hover:bg-charcoal/40 transition-colors">
              <span className="font-mono text-[0.6rem] font-bold tracking-[0.35em] uppercase text-coral">
                {item?.type}
              </span>
              <span className="font-sans text-sm text-concrete/50 font-medium">
                {item?.desc}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
